import { cn } from "@msuite/ui/utils";
import { isHoliday, type Moment, moment } from "@msuite/utils/date";
import { useMemo } from "react";
import { usePlanStore } from "@/components/plan/store";

export type CapacityCalendarController = ReturnType<typeof useCapacityCalendar>;

export interface CapacityCalendarCell {
	cellMoment: Moment;
	/** The day as the database stores it, so a row compares strings rather than formatting again. */
	date: string;
	isWeekend: boolean;
	isHoliday: boolean;
	isToday: boolean;
	/**
	 * The classes the day strip paints this cell with. Resolved here, once per window, because every
	 * row of the board draws the same strip and merging Tailwind classes per cell per row was most of
	 * what a row cost to render.
	 */
	className: string;
}

const DAYS_PER_WEEK = 7;
const FIRST_COLUMN_WIDTH = "500px";

/**
 * The window every row of the capacity board is drawn against: the week the plan header stands on
 * and however many follow it, derived from the week picker rather than a picker of its own.
 *
 * How many weeks is one store value, so nothing here counts columns for itself — the day array,
 * the header's week spans, the range caption and the grid template all derive from it, and the
 * bar gradients tile by dividing 100 by the number of days they are handed.
 */
export function useCapacityCalendar() {
	/** Store */
	const currentWeek = usePlanStore((state) => state.currentWeek);
	const weekCount = usePlanStore((state) => state.capacityWeeks);

	/** Memoized */
	const columns = weekCount * DAYS_PER_WEEK;
	const start = useMemo(() => currentWeek.clone().startOf("week"), [currentWeek]);

	const cells = useMemo<CapacityCalendarCell[]>(() => {
		const today = moment();
		const result: CapacityCalendarCell[] = new Array(columns);
		for (let index = 0; index < columns; index++) {
			const cellMoment = start.clone().add(index, "day");
			const date = cellMoment.db();
			const isWeekend = cellMoment.isoWeekday() >= 6;
			const isTodayCell = cellMoment.isSame(today, "day");
			const isHolidayCell = isHoliday(date);
			result[index] = {
				cellMoment,
				date,
				isWeekend,
				isHoliday: isHolidayCell,
				isToday: isTodayCell,
				className: cn(
					"w-full h-full",
					isWeekend && "bg-gray-100 dark:bg-zinc-800",
					isHolidayCell && "stripes",
					isTodayCell && "bg-primary-50 dark:bg-primary-950/50",
				),
			};
		}
		return result;
	}, [start, columns]);

	const weeks = useMemo<Moment[]>(() => {
		const result: Moment[] = new Array(weekCount);
		for (let index = 0; index < weekCount; index++) {
			result[index] = start.clone().add(index, "week");
		}
		return result;
	}, [start, weekCount]);

	// The window's two ends as stored dates, so anything asking "does this reach the board" compares
	// strings rather than rebuilding the span from the cells.
	const windowStart = useMemo(() => start.db(), [start]);
	const windowEnd = useMemo(
		() =>
			start
				.clone()
				.add(columns - 1, "day")
				.db(),
		[start, columns],
	);

	const rangeString = useMemo(() => {
		const end = start.clone().add(columns - 1, "day");
		return `${start.format("DD.MM.")} – ${end.format("DD.MM.YYYY")}`;
	}, [start, columns]);

	const constants = useMemo(
		() => ({
			FIRST_COLUMN_WIDTH,
			COLUMNS: columns,
			DAYS_PER_WEEK,
			WEEKS: weekCount,
			gridTemplateColumns: `${FIRST_COLUMN_WIDTH} repeat(${columns}, minmax(0, 1fr))`,
		}),
		[columns, weekCount],
	);

	return useMemo(
		() => ({ start, cells, weeks, windowStart, windowEnd, rangeString, constants }),
		[start, cells, weeks, windowStart, windowEnd, rangeString, constants],
	);
}
